import { apiClient } from '@/lib/api';
import type {
  PricerSolarHistoryEntry,
  PricerSolarQuote,
  PricerSolarStatus,
  SolarCatalog,
  SolarPayload,
  SolarResults,
} from '../types/pricer-solar.types';

const BASE = '/api/pricer-solar';

class PricerSolarService {
  async getCatalog(): Promise<SolarCatalog> {
    return apiClient.get<SolarCatalog>(`${BASE}/catalog`);
  }

  async updateConfig(config: Partial<SolarCatalog>): Promise<SolarCatalog> {
    return apiClient.patch<SolarCatalog>(`${BASE}/config`, config);
  }

  async calculate(payload: SolarPayload): Promise<SolarResults> {
    return apiClient.post<SolarResults>(`${BASE}/calculate`, payload);
  }

  async listQuotes(params?: {
    status?: PricerSolarStatus;
    search?: string;
  }): Promise<PricerSolarQuote[]> {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.search) q.set('search', params.search);
    const qs = q.toString();
    return apiClient.get<PricerSolarQuote[]>(`${BASE}/quotes${qs ? `?${qs}` : ''}`);
  }

  async getQuote(id: string): Promise<PricerSolarQuote> {
    return apiClient.get<PricerSolarQuote>(`${BASE}/quotes/${id}`);
  }

  async createQuote(
    payload: SolarPayload
  ): Promise<{ quote: PricerSolarQuote; results: SolarResults }> {
    return apiClient.post(`${BASE}/quotes`, payload);
  }

  async updateQuote(
    id: string,
    payload: Partial<SolarPayload>
  ): Promise<{ quote: PricerSolarQuote; results: SolarResults }> {
    return apiClient.patch(`${BASE}/quotes/${id}`, payload);
  }

  async submitQuote(id: string): Promise<PricerSolarQuote> {
    return apiClient.post<PricerSolarQuote>(`${BASE}/quotes/${id}/submit`);
  }

  async deleteQuote(id: string): Promise<void> {
    return apiClient.delete<void>(`${BASE}/quotes/${id}`);
  }

  async approveQuote(id: string, comments?: string): Promise<PricerSolarQuote> {
    return apiClient.post<PricerSolarQuote>(`${BASE}/quotes/${id}/approve`, { comments });
  }

  async rejectQuote(id: string, comments: string): Promise<PricerSolarQuote> {
    return apiClient.post<PricerSolarQuote>(`${BASE}/quotes/${id}/reject`, { comments });
  }

  async requestAdjustment(id: string, comments: string): Promise<PricerSolarQuote> {
    return apiClient.post<PricerSolarQuote>(`${BASE}/quotes/${id}/request-adjustment`, {
      comments,
    });
  }

  async cancelQuote(id: string, comments?: string): Promise<PricerSolarQuote> {
    return apiClient.post<PricerSolarQuote>(`${BASE}/quotes/${id}/cancel`, { comments });
  }

  async addComment(id: string, message: string): Promise<PricerSolarHistoryEntry> {
    return apiClient.post<PricerSolarHistoryEntry>(`${BASE}/quotes/${id}/comments`, { message });
  }

  async getHistory(id: string): Promise<PricerSolarHistoryEntry[]> {
    return apiClient.get<PricerSolarHistoryEntry[]>(`${BASE}/quotes/${id}/history`);
  }
}

export const pricerSolarService = new PricerSolarService();
export default pricerSolarService;
